import { createEntityAdapter, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { loadUsersAndPersonalResources } from './thunks/loadUsersAndPersonalResources';
import { loadUsersAndPersonalResourcesByUserIds } from './thunks/loadUsersAndPersonalResourcesByUserIds';
import { loadEmployeeConfigurationsInBusiness } from './thunks/loadEmployeeConfigurationsInBusiness.thunk';
import { groupBy } from 'lodash-es';
import {
  clearLatestOrientationFlowResult,
  closeAddNewUserDialog,
  closeJoinEmployeeToProjectDialog,
  openAddNewUserDialog,
  openJoinEmployeeToProjectDialog,
  setJoinEmployeeToProjectSelectedBusinessId,
} from './actions/project-users.actions';
import { createOrUpdateUser } from 'store/user/thunks/createOrUpdateUser.thunk';
import { joinExistingEmployeeToProject } from 'store/resources/thunks/joinExistingEmployeeToProject.thunk';
import { enqueueSnackbar } from 'notistack';

const usersAdapter = createEntityAdapter();
const userPersonalResourcesAdapter = createEntityAdapter();

const projectUsersSlice = createSlice({
  name: 'projectUsers',
  initialState: {
    users: usersAdapter.getInitialState(),
    userPersonalResources: userPersonalResourcesAdapter.getInitialState(),
    employeeBusinessConfigurationsByBusinessId: {},
    loading: false,
    isAddNewUserDialogOpen: false,
    addNewUserDialogMeta: null,
    isJoinEmployeeToProjectDialogOpen: false,
    joinEmployeeToProjectDialogMeta: null,
    joinEmployeeSelectedBusinessId: '',
    latestOrientationFlowResult: null,
  },
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(loadUsersAndPersonalResources.pending, (state) => {
        state.loading = true;
      })

      .addCase(loadUsersAndPersonalResources.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
      })
      .addCase(
        loadEmployeeConfigurationsInBusiness.fulfilled,
        (state, action) => {
          state.employeeBusinessConfigurationsByBusinessId = groupBy(
            action.payload,
            'businessId'
          );
        }
      )
      .addCase(openAddNewUserDialog, (state, action) => {
        state.isAddNewUserDialogOpen = true;
        state.addNewUserDialogMeta = action.payload || null;
      })
      .addCase(closeAddNewUserDialog, (state, action) => {
        state.isAddNewUserDialogOpen = false;
        state.addNewUserDialogMeta = null;
      })
      .addCase(clearLatestOrientationFlowResult, (state) => {
        state.latestOrientationFlowResult = null;
      })
      .addCase(createOrUpdateUser.fulfilled, (state, action) => {
        state.isAddNewUserDialogOpen = false;
        state.addNewUserDialogMeta = null;

        state.latestOrientationFlowResult = {
          requestId: action.meta.requestId,
          source: 'create_user',
          projectId: action.meta.arg?.projectId || null,
          targetUserId: action.payload?.resource?.user || null,
          user: {
            id: action.payload?.resource?.user || null,
            firstName: action.meta.arg?.userAttributes?.firstName || '',
            lastName: action.meta.arg?.userAttributes?.lastName || '',
            phone:
              action.meta.arg?.userAttributes?.phoneNumber ||
              action.payload?.resource?.phone ||
              '',
            email: action.payload?.resource?.email || '',
          },
          resource: action.payload?.resource || null,
          employer: action.payload?.employer || null,
          recordDate: action.meta.arg?.recordDate || null,
        };
      })
      .addCase(openJoinEmployeeToProjectDialog, (state, action) => {
        state.isJoinEmployeeToProjectDialogOpen = true;
        if (typeof action.payload === 'string') {
          state.joinEmployeeSelectedBusinessId = action.payload;
          state.joinEmployeeToProjectDialogMeta = null;
        } else {
          state.joinEmployeeToProjectDialogMeta = action.payload || null;
          if (action.payload?.selectedBusinessId) {
            state.joinEmployeeSelectedBusinessId =
              action.payload.selectedBusinessId;
          }
        }
      })
      .addCase(setJoinEmployeeToProjectSelectedBusinessId, (state, action) => {
        state.joinEmployeeSelectedBusinessId = action.payload;
      })

      .addCase(closeJoinEmployeeToProjectDialog, (state, action) => {
        state.isJoinEmployeeToProjectDialogOpen = false;
        state.joinEmployeeToProjectDialogMeta = null;
      })
      .addCase(joinExistingEmployeeToProject.fulfilled, (state, action) => {
        state.isJoinEmployeeToProjectDialogOpen = false;
        state.joinEmployeeToProjectDialogMeta = null;

        state.latestOrientationFlowResult = {
          requestId: action.meta.requestId,
          source: 'join_existing',
          projectId: action.meta.arg?.projectId || null,
          targetUserId:
            action.payload?.resource?.user || action.meta.arg?.userId || null,
          user: action.meta.arg?.selectedUserData || null,
          resource: action.payload?.resource || null,
          employer: action.payload?.resourceEmployer || null,
          recordDate: action.meta.arg?.recordDate || null,
        };

        enqueueSnackbar('Työntekijä liitetty projektiin', {
          variant: 'success',
        });
      })
      .addCase(joinExistingEmployeeToProject.rejected, (state, action) => {
        state.isJoinEmployeeToProjectDialogOpen = false;
        state.joinEmployeeToProjectDialogMeta = null;
        enqueueSnackbar('Työntekijän liittäminen projektiin epäonnistui', {
          variant: 'error',
        });
      })
      .addMatcher(
        isAnyOf(
          loadUsersAndPersonalResources.fulfilled,
          loadUsersAndPersonalResourcesByUserIds.fulfilled
        ),
        (state, action) => {
          const users = action.payload.users;

          const userPersonalResources = action.payload.userPersonalResources;

          usersAdapter.setMany(state.users, users);
          usersAdapter.setMany(
            state.userPersonalResources,
            userPersonalResources
          );
          state.loading = false;
        }
      );
  },
});

export default projectUsersSlice.reducer;
